import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Rate the winning vendor. Auto-loads existing review if the resident already left one. */
export default function VendorRatingDialog({ open, onOpenChange, ticket, onSaved }) {
  const vendorId = ticket?.assigned_vendor_id;
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !ticket) return;
    setStars(0); setHover(0); setComment(""); setExisting(null);
    (async () => {
      try {
        const { data } = await api.get(`/maintenance/${ticket.id}/review`);
        if (data.review) {
          setExisting(data.review);
          setStars(data.review.stars);
          setComment(data.review.comment || "");
        }
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/VendorRatingDialog.jsx: swallowed', err); }
    })();
  }, [open, ticket]);

  const submit = async () => {
    if (!vendorId) return toast.error("No vendor to review on this ticket");
    if (!stars) return toast.error("Choose 1–5 stars");
    setSaving(true);
    try {
      await api.post(`/vendors/${vendorId}/reviews`, {
        ticket_id: ticket.id, stars, comment: comment.trim() || null,
      });
      toast.success("Review submitted — thanks!");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rate {ticket?.assigned_vendor || "vendor"}</DialogTitle>
        </DialogHeader>
        {existing && (
          <div className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 p-2">
            You already reviewed this ticket. Your existing rating is shown below — new submissions are disabled.
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-3" data-testid="rating-stars">
          {[1,2,3,4,5].map((n) => {
            const filled = (hover || stars) >= n;
            return (
              <button
                key={n}
                type="button"
                disabled={!!existing}
                data-testid={`star-${n}`}
                onClick={() => setStars(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className={"h-10 w-10 rounded-md flex items-center justify-center transition-colors " +
                  (existing ? "opacity-70 cursor-not-allowed " : "hover:bg-amber-50 cursor-pointer ")}
              >
                <Star className={"h-7 w-7 " + (filled ? "text-amber-400 fill-amber-400" : "text-zinc-300")} />
              </button>
            );
          })}
          <div className="ml-3 text-sm text-zinc-600 tabular-nums">
            {stars ? `${stars}.0 / 5` : "Tap a star"}
          </div>
        </div>
        <div className="mt-3">
          <Textarea
            data-testid="rating-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Anything future residents should know about this vendor?"
            rows={4}
            disabled={!!existing}
          />
        </div>
        <Button
          data-testid="rating-submit"
          onClick={submit}
          disabled={saving || !!existing}
          className="w-full mt-3 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (existing ? "Already reviewed" : "Submit review")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
